import React from 'react';

const TeamMember: React.FC<{
  name: string;
  role: string;
  emoji: string;
  color: string;
}> = ({ name, role, emoji, color }) => {
  return (
    <div className={`card-cute cute-hover ${color}`}>
      <div className="text-4xl mb-3">{emoji}</div>
      <h3 className="text-xl font-bold">{name}</h3>
      <p className="text-sm">{role}</p>
    </div>
  );
};

const AboutPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="heading-cute text-4xl mb-4 bounce-on-hover">About Berry Babies</h1>
        <div className="w-32 h-1 bg-primary mx-auto rounded-full mb-6"></div>
      </div>

      {/* Story Section */}
      <section className="card-cute mb-10 bg-opacity-50 backdrop-blur-sm">
        <h2 className="heading-cute text-2xl mb-4">Our Berry Sweet Story</h2>
        <p className="mb-4">
          Berry Babies was born out of love and necessity when our founder, Berry Berry, couldn't 
          find adorable yet practical baby products for her twins. What started as handmade gifts 
          for friends and family quickly blossomed into a beloved brand trusted by parents everywhere.
        </p>
        <p>
          We believe that childhood should be filled with color, joy, and playfulness. Every Berry 
          Babies product is designed to bring smiles to both parents and little ones while providing 
          the quality and functionality that modern families need.
        </p>
      </section>

      {/* Berries Divider */}
      <div className="flex justify-center mb-10">
        <div className="flex space-x-3">
          {['💜', '💚', '💛', '💜', '💚', '💛'].map((heart, index) => (
            <span 
              key={index} 
              className="text-2xl animate-bounce" 
              style={{ animationDelay: `${index * 0.15}s`, animationDuration: '1s' }}
            >
              {heart}
            </span>
          ))}
        </div>
      </div>

      {/* Mission */}
      <section className="card-cute mb-10 bg-secondary text-secondary-foreground">
        <h2 className="heading-cute text-2xl mb-4 text-white">Our Berry Mission</h2>
        <ul className="space-y-3">
          <li className="flex items-start">
            <span className="mr-2 text-xl">🍓</span>
            <p>To create the cutest, most functional baby products that grow with your child</p>
          </li>
          <li className="flex items-start">
            <span className="mr-2 text-xl">🫐</span>
            <p>To use only safe, eco-friendly materials that are gentle on baby's skin and the planet</p>
          </li>
          <li className="flex items-start">
            <span className="mr-2 text-xl">🍒</span>
            <p>To support parents on their journey with products that make life easier and more colorful</p>
          </li>
          <li className="flex items-start">
            <span className="mr-2 text-xl">🍇</span>
            <p>To spread berry joy to little ones everywhere through playful designs and quality craftsmanship</p>
          </li>
        </ul>
      </section>

      {/* Team */}
      <section className="mb-10">
        <h2 className="heading-cute text-3xl text-center mb-6">Meet Our Berry Team</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          <TeamMember 
            name="Berry Berry" 
            role="Founder & CEO" 
            emoji="👩‍🦰" 
            color="bg-pink"
          />
          <TeamMember 
            name="Beary Blue" 
            role="Design Director" 
            emoji="👨‍🎨" 
            color="bg-blue"
          />
          <TeamMember 
            name="Strawby Fields" 
            role="Product Manager" 
            emoji="👩‍💼" 
            color="bg-green"
          />
          <TeamMember 
            name="Razzy Dazzle" 
            role="Customer Happiness" 
            emoji="👨‍💻" 
            color="bg-yellow"
          />
        </div>
      </section>

      {/* Values */}
      <section className="card-cute bg-primary text-primary-foreground mb-10">
        <h2 className="heading-cute text-2xl mb-4 text-white">Berry Values</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white bg-opacity-20 p-4 rounded-lg">
            <h3 className="font-bold mb-2">Quality</h3>
            <p>We never compromise on materials or craftsmanship</p>
          </div>
          <div className="bg-white bg-opacity-20 p-4 rounded-lg">
            <h3 className="font-bold mb-2">Cuteness</h3>
            <p>Life's too short for boring baby products</p>
          </div>
          <div className="bg-white bg-opacity-20 p-4 rounded-lg">
            <h3 className="font-bold mb-2">Sustainability</h3>
            <p>Kind to babies and kind to their future planet</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;